import { useQuery, useZero } from "@rocicorp/zero/react";
import { findConnectorTypes } from "../../../shared/connectors/findConnectorTypes";
import { makeBranchParams } from "../../../shared/copyOnWrite";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { SUB_AGENT_USER_TOOLS } from "../../../shared/subAgent";
import { useCurrentUserId } from "../../hooks/useCurrentUserId";
import type { SelectableConnector } from "../../hooks/useConnectorSelectOnSave";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useIsGitSynced } from "../../hooks/useIsGitSynced";
import { blockedBySelfMerge } from "../../lib/blockedBySelfMerge";
import { emitErrorToast } from "../../lib/errorToast";
import { uploadToolCallOutput } from "../../lib/toolCallOutputs";
import { navigate, routes } from "../../lib/urls";
import {
  connectedOutput,
  useConnectNotification,
} from "../../tools/ConnectToApp";
import {
  ConnectorPrompt,
  mutationErrorMessage,
} from "../../tools/ConnectorPrompt";
import { PushLivePrompt, parseSummary } from "../../tools/PushLive";

type PendingSubAgentCall = {
  tool: string;
  workflowId: string;
  branchId: string;
  chatId: string;
  entryId: string;
  input: unknown;
};

export function usePendingSubAgentCall(
  chatId: string
): PendingSubAgentCall | null {
  const [agents] = useQuery(queries.subAgentsForChat({ chatId }));
  const agent = agents.find(
    (agent) =>
      agent.status === "needs_input" &&
      agent.outcome?.status === "needs_input" &&
      SUB_AGENT_USER_TOOLS.has(agent.outcome.pending.tool)
  );
  const [commits] = useQuery(
    queries.loadBranchChats({ branchId: agent?.branchId ?? "" })
  );
  if (!agent?.branchId || agent.outcome?.status !== "needs_input") return null;
  const { entryId, tool, input } = agent.outcome.pending;
  const chat = commits.find((commit) =>
    commit.chats?.entries.some(
      (entry) => entry.id === entryId && entry.toolCall?.completedAt == null
    )
  )?.chats;
  return chat
    ? {
        tool,
        workflowId: agent.workflowId,
        branchId: agent.branchId,
        chatId: chat.id,
        entryId,
        input,
      }
    : null;
}

export function SubAgentPrompt(call: PendingSubAgentCall) {
  return call.tool === "pushLive" ? (
    <SubAgentPushLive {...call} />
  ) : (
    <SubAgentConnector {...call} />
  );
}

export function useProvideChildToolResult({
  chatId,
  entryId,
  branchId,
}: Pick<PendingSubAgentCall, "chatId" | "entryId" | "branchId">) {
  const z = useZero();
  return async (
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean }
  ) => {
    const saved = await uploadToolCallOutput({ chatId, entryId, output })
      .then(
        (outputId) =>
          z.mutate(
            sharedMutators.provideSpaceChatToolResult({
              chatId,
              entryId,
              outputId,
              isError,
              branchId,
              pauseAfter: options?.pauseAfter,
            })
          ).server
      )
      .then(
        (result) => result.type === "success",
        () => false
      );
    if (!saved)
      emitErrorToast({
        icon: "circle-off",
        message: "The tool result couldn’t be saved.",
        buttonLabel: "Dismiss",
      });
    return saved;
  };
}

function SubAgentPushLive(call: PendingSubAgentCall) {
  const { workflowId, branchId } = call;
  const currentUserId = useCurrentUserId();
  const onResult = useProvideChildToolResult(call);
  const [workflow] = useQuery(queries.loadWorkflow({ workflowId }));
  const [branch] = useQuery(
    queries.loadBranchMetadata({ workflowId, branchId })
  );
  const spaceId = workflow?.spaceId ?? "";
  const canPushLive = useHasAccess("space:push_live", spaceId);
  const canWrite = useHasAccess("space:write", spaceId);
  const gitSynced = useIsGitSynced(spaceId);
  if (!workflow || !branch) return null;

  return (
    <PushLivePrompt
      branch={{ id: branch.id, name: branch.name, workflowId }}
      mainBranchId={workflow.branches.find((b) => b.name === "main")?.id}
      spaceId={spaceId}
      canPushLive={canPushLive}
      canWrite={canWrite}
      blockedBySelfMerge={blockedBySelfMerge(workflow, branch, currentUserId)}
      gitSynced={gitSynced}
      onResult={onResult}
      toolCall={{ chatId: call.chatId, entryId: call.entryId }}
      workflowName={workflow.name}
      summary={parseSummary(call.input)}
      stayAfterPush
    />
  );
}

function SubAgentConnector(call: PendingSubAgentCall) {
  const { workflowId, branchId, entryId } = call;
  const z = useZero();
  const onResult = useProvideChildToolResult(call);
  const [workflow] = useQuery(queries.loadWorkflow({ workflowId }));
  const { step, targetUrl, search, connectorId } = call.input as {
    step?: string;
    targetUrl?: string;
    search?: string;
    connectorId?: string;
  };
  const showPrompt = !!workflow && !!(connectorId || targetUrl || search);
  useConnectNotification({ step, targetUrl, search }, showPrompt);
  if (!showPrompt) return null;

  const selectApp = async (app: SelectableConnector) => {
    const { server } = z.mutate(
      call.tool === "connectStepToApp" && step
        ? sharedMutators.associateStepWithConnectedApp({
            ...makeBranchParams(branchId),
            stepDir: step,
            connectedAppId: app.id,
            silent: true,
          })
        : sharedMutators.associateBranchChatWithConnectedApp({
            branchId,
            connectedAppId: app.id,
          })
    );
    const result = await server;
    if (result.type === "error")
      await onResult({ error: mutationErrorMessage(result.error) }, true);
    else await onResult(connectedOutput(app, step), false);
  };

  return (
    <ConnectorPrompt
      pending={{
        toolCallId: entryId,
        step,
        targetUrl,
        search,
        connectorId,
        appIdentifiers: findConnectorTypes({ search, targetUrl }).map(
          (app) => app.identifier
        ),
      }}
      spaceId={workflow.spaceId}
      openCreatedApp={(id) => navigate(routes.modal.connector, { id })}
      onSelectApp={selectApp}
      onDismiss={() => void onResult({ dismissed: true }, false)}
      onError={(message) => void onResult({ error: message }, true)}
      onTypeInstead={() =>
        void onResult({ dismissed: true }, false, { pauseAfter: true })
      }
    />
  );
}
